import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Plus, Pencil, Trash2, KeyRound, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";

export default function StoresList() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const isAdmin = user?.group === "admin";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/stores");
      setStores(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const remove = async (s) => {
    if (!confirm(t("stores.deleteConfirm", { name: s.name }))) return;
    try {
      await api.delete(`/stores/${s.id}`);
      toast.success(t("stores.deleted"));
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const testFtp = async (s) => {
    toast.loading(t("stores.testing"), { id: "ftp" });
    try {
      const { data } = await api.post(`/stores/${s.id}/test-ftp`);
      toast.dismiss("ftp");
      if (data.ok) toast.success(data.message);
      else toast.error(data.message);
    } catch (e) {
      toast.dismiss("ftp");
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid="stores-list-page">
      <div className="flex items-end justify-between mb-7 flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.32em] text-slate-400 mb-1">
            {t("stores.breadcrumb")}
          </div>
          <h1 className="text-3xl font-extrabold text-slate-900">{t("stores.title")}</h1>
        </div>
        {isAdmin && (
          <Link
            to="/stores/new"
            className="px-4 py-2.5 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-semibold text-sm flex items-center gap-2"
            data-testid="add-store-btn"
          >
            <Plus size={16} /> {t("stores.addStore")}
          </Link>
        )}
      </div>

      <div className="pg-card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-widest text-slate-500 bg-slate-50/70">
              <th className="px-5 py-3 font-semibold">{t("stores.columns.name")}</th>
              <th className="px-5 py-3 font-semibold">{t("stores.columns.machineId")}</th>
              <th className="px-5 py-3 font-semibold">{t("stores.columns.type")}</th>
              <th className="px-5 py-3 font-semibold">{t("stores.columns.gst")}</th>
              <th className="px-5 py-3 font-semibold">{t("stores.columns.ftp")}</th>
              <th className="px-5 py-3 font-semibold">{t("stores.columns.status")}</th>
              <th className="px-5 py-3 font-semibold text-right">{t("stores.columns.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} className="px-5 py-10 text-center text-slate-400">
                  {t("common.loading")}
                </td>
              </tr>
            ) : stores.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-5 py-10 text-center text-slate-400">
                  {t("stores.noStores")}
                </td>
              </tr>
            ) : (
              stores.map((s) => (
                <tr
                  key={s.id}
                  className="border-t border-slate-100 hover:bg-slate-50/50"
                  data-testid={`store-row-${s.id}`}
                >
                  <td className="px-5 py-4">
                    <Link
                      to={`/stores/${s.id}/files`}
                      className="font-semibold text-blue-700 hover:text-cyan-600"
                    >
                      {s.name}
                    </Link>
                    {s.uid && (
                      <div className="text-xs text-slate-400 mt-0.5">{s.uid}</div>
                    )}
                  </td>
                  <td className="px-5 py-4 mono text-slate-700">{s.machine_id}</td>
                  <td className="px-5 py-4">
                    <span className="pill pill-info">{s.store_type}</span>
                  </td>
                  <td className="px-5 py-4">
                    {s.has_gst === "no" ? (
                      <span className="pill pill-info">{t("stores.noGst")}</span>
                    ) : (
                      <span className="pill pill-warning">
                        {s.has_gst === "retail" ? t("stores.gstRetail") : t("stores.gstFnb")}
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-4 text-slate-600">
                    {s.ftp_host ? (
                      <div className="text-xs">
                        <div className="mono">{s.ftp_host}:{s.ftp_port}</div>
                        <div className="text-slate-400">
                          {s.is_sftp ? "sFTP" : "FTP"}
                        </div>
                      </div>
                    ) : (
                      <span className="text-slate-400 text-xs">{t("stores.ftpNotConfigured")}</span>
                    )}
                  </td>
                  <td className="px-5 py-4">
                    <span className={s.active ? "pill pill-success" : "pill pill-danger"}>
                      {s.active ? t("common.active") : t("common.inactive")}
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-2 justify-end">
                      {isAdmin && (
                        <button
                          onClick={() => navigate(`/stores/${s.id}/data-analysis`)}
                          className="p-2 rounded-md text-slate-500 hover:bg-blue-50 hover:text-blue-600 transition"
                          title={t("dataAnalysis.title")}
                          aria-label={t("dataAnalysis.title")}
                          data-testid={`data-analysis-${s.id}`}
                        >
                          <BarChart3 size={16} />
                        </button>
                      )}
                      {isAdmin && (
                        <button
                          onClick={() => testFtp(s)}
                          className="p-2 rounded-md text-slate-500 hover:bg-cyan-50 hover:text-cyan-600 transition"
                          title={t("stores.testFtp")}
                          aria-label={t("stores.testFtp")}
                          data-testid={`test-ftp-${s.id}`}
                        >
                          <KeyRound size={16} />
                        </button>
                      )}
                      {isAdmin && (
                        <button
                          onClick={() => navigate(`/stores/${s.id}/edit`)}
                          className="p-2 rounded-md text-slate-500 hover:bg-blue-50 hover:text-blue-600 transition"
                          title={t("stores.edit")}
                          aria-label={t("stores.edit")}
                          data-testid={`edit-store-${s.id}`}
                        >
                          <Pencil size={16} />
                        </button>
                      )}
                      {isAdmin && (
                        <button
                          onClick={() => remove(s)}
                          className="p-2 rounded-md text-slate-500 hover:bg-rose-50 hover:text-rose-600 transition"
                          title={t("stores.delete")}
                          aria-label={t("stores.delete")}
                          data-testid={`delete-store-${s.id}`}
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
